import { useEffect, useState } from 'react';

import { fontStyles } from '@/styles/typography';

import { classNames } from '@/utils/classnames';

/** ⌘K on Apple platforms, ^K (Ctrl) elsewhere. */
const getPlatformShortcutLabel = () =>
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent) ? '⌘K' : '^K';

const keycapBadge = classNames(
  'text-token-gray-800 bg-token-alphalight-300 border-token-alphalight-300',
  'flex h-6 items-center justify-center rounded-xs border border-solid',
);

/**
 * Keycap hint for the token-search shortcuts, shared by the header and hero
 * search bars. Always shows the "/" badge; once mounted it also shows the
 * platform combo (⌘K on macOS, ^K elsewhere) — both open search (see
 * `useSearchShortcut`).
 *
 * The platform badge is client-only because the platform is unknown during
 * SSR; rendering only "/" on the server keeps the hydrated markup identical,
 * and the combo is appended to the left so the "/" badge never shifts.
 *
 * The group is `aria-hidden`: the glyphs (⌘, ^, and the "/" SVG) don't
 * announce cleanly, so the shortcut is exposed to assistive tech via
 * `aria-keyshortcuts` on the search button instead.
 *
 * Pass display and positioning classes via `className` (e.g. `flex absolute
 * right-4`); sizing, colors, and border are owned by the component.
 */
export const SearchShortcutKeycap = ({ className }: { className?: string }) => {
  const [platformLabel, setPlatformLabel] = useState<string | null>(null);

  useEffect(() => {
    setPlatformLabel(getPlatformShortcutLabel());
  }, []);

  return (
    <span
      aria-hidden="true"
      className={classNames('items-center gap-1 pointer-events-none', className)}
    >
      {platformLabel && (
        <kbd className={fontStyles({ variant: 'LABEL_XS', class: [keycapBadge, 'px-1.5'] })}>
          {platformLabel}
        </kbd>
      )}
      <kbd className={classNames(keycapBadge, 'w-6')}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.9368 2.70532L7.88991 14.0249H5.96484L9.01172 2.70532H10.9368Z"
            fill="currentColor"
          />
        </svg>
      </kbd>
    </span>
  );
};
